import { useMemo, useState } from "react";
import { useAvatarUrl } from "@/lib/media";

// Initials fallback with a stable per-user color gradient.
// - Uses initials from display_name (or username as a fallback)
// - Colors are derived from a hash of the identifier so the same user always
//   gets the same background
// - When the image fails to load or is missing we render the initials — never
//   a broken image or a giant "?" glyph.

const PALETTES = [
  ["#f43f5e", "#f97316"],
  ["#ec4899", "#a855f7"],
  ["#8b5cf6", "#3b82f6"],
  ["#3b82f6", "#06b6d4"],
  ["#06b6d4", "#22c55e"],
  ["#22c55e", "#eab308"],
  ["#eab308", "#f97316"],
  ["#ef4444", "#ec4899"],
  ["#0ea5e9", "#6366f1"],
  ["#14b8a6", "#22c55e"],
];

function stableHash(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

function initialsOf(name = "") {
  const clean = String(name).trim();
  if (!clean) return "?";
  const parts = clean.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    const p = parts[0].replace(/[^\p{L}\p{N}]/gu, "");
    return (p[0] || "?").toUpperCase() + (p[1] || "").toUpperCase();
  }
  return ((parts[0][0] || "") + (parts[parts.length - 1][0] || "")).toUpperCase();
}

const SIZE_CLASS = {
  xs: "w-6 h-6 text-[10px]",
  sm: "w-8 h-8 text-[11px]",
  md: "w-10 h-10 text-xs",
  lg: "w-11 h-11 text-sm",
  xl: "w-14 h-14 text-base",
  "2xl": "w-20 h-20 text-lg",
  "3xl": "w-28 h-28 sm:w-32 sm:h-32 text-2xl",
};

export default function UserAvatar({
  user,
  displayName,
  username,
  src,
  size = "md",
  className = "",
  ring = false,
  testid,
}) {
  const [broken, setBroken] = useState(false);

  // useAvatarUrl handles both inline URL fields AND `profile_photo_media_id`
  // (used by /me/saved and other joined-in-Xano responses) — resolving the
  // media_id via GET /media/{id} with an in-memory cache.
  const asyncUrl = useAvatarUrl(user);
  const resolved = useMemo(() => {
    if (src) return src;
    return asyncUrl || "";
  }, [src, asyncUrl]);

  const name = displayName || user?.display_name || user?.name || user?.username || username || "";
  const key = user?.username || user?.id || username || name || "user";
  const [c1, c2] = PALETTES[stableHash(String(key)) % PALETTES.length];
  const initials = initialsOf(name || username || "");

  const sizeClass = SIZE_CLASS[size] || SIZE_CLASS.md;
  const showImage = resolved && !broken;

  return (
    <div
      className={`relative rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center font-medium select-none ${sizeClass} ${
        ring ? "ring-2 ring-background shadow-sm" : ""
      } ${className}`}
      style={
        showImage
          ? undefined
          : {
              background: `linear-gradient(135deg, ${c1}, ${c2})`,
              color: "#fff",
            }
      }
      data-testid={testid}
      aria-label={name || username || "User"}
    >
      {showImage ? (
        <img
          src={resolved}
          alt={name || username || "User"}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={() => setBroken(true)}
        />
      ) : (
        <span className="tracking-tight">{initials}</span>
      )}
    </div>
  );
}
